import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../i18n';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, Divider, FormControlLabel, IconButton,
  InputBase, Menu, MenuItem, ListItemIcon, Skeleton, Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PublicIcon from '@mui/icons-material/Public';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { auditsApi } from '../services/audits';
import { proposedArticlesApi } from '../services/proposed-articles';
import { feedsApi } from '../services/integrations';
import {
  CHANNEL_GROUPS, CHANNEL_LABEL, FORMAT_OPTIONS, PATTERN_OPTIONS,
  OBJECTIVE_OPTIONS, MOMENTUM_META,
} from '../components/news/channels';
import { ChannelLogo } from '../components/news/ChannelLogo';
import {
  NEWS_WATCH_CHANNEL_COST, CONTENT_SEED_LIMITS, CONTENT_PROVIDE_MIN_WORDS, seedWordCount,
  isProvideSeedSufficient,
} from '@traaker/shared';
import type {
  ProposedArticle, ProposedArticleStatus, ContentChannel, ContentFormat, ContentPattern,
  ContentObjective, ContentSourceMode, ContentCreationConfig, ContentTopic,
} from '@traaker/shared';

const PAGE_SIZE = 10;

// `title`/`desc` are i18n KEYS (prefix `newsWatch.`) — resolved with `t(...)` in
// the consuming component, not display strings.
const SOURCE_MODES: { value: ContentSourceMode; icon: string; title: string; desc: string }[] = [
  { value: 'propose', icon: '📰', title: 'newsWatch.source.propose.title', desc: 'newsWatch.source.propose.desc' },
  { value: 'provide', icon: '✍️', title: 'newsWatch.source.provide.title', desc: 'newsWatch.source.provide.desc' },
  { value: 'reco', icon: '💡', title: 'newsWatch.source.reco.title', desc: 'newsWatch.source.reco.desc' },
];

const PURPLE = '#7C3AED';

function relativeDate(iso?: string): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return i18n.t('newsWatch.relative.today');
  if (days === 1) return i18n.t('newsWatch.relative.yesterday');
  if (days < 7) return i18n.t('newsWatch.relative.daysAgo', { count: days });
  const w = Math.floor(days / 7);
  if (w < 5) return i18n.t('newsWatch.relative.weeksAgo', { count: w });
  return i18n.t('newsWatch.relative.monthsAgo', { count: Math.floor(days / 30) });
}

export default function NewsWatch() {
  const { t } = useTranslation();
  const { activeBrand } = useBrand();
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgency = tenant?.tier === 'agency';

  const [sourceMode, setSourceMode] = useState<ContentSourceMode>('propose');
  const [seed, setSeed] = useState('');
  // Saved content topics (content-type recommendations acted on) + the one
  // picked for `reco` source mode.
  const [contentTopics, setContentTopics] = useState<ContentTopic[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [format, setFormat] = useState<ContentFormat>('standard');
  const [channels, setChannels] = useState<ContentChannel[]>(['blog_seo', 'linkedin_post', 'x_thread']);
  const [patterns, setPatterns] = useState<ContentPattern[]>(['storytelling']);
  const [objective, setObjective] = useState<ContentObjective>('authority');
  const [launching, setLaunching] = useState(false);
  const [genPopup, setGenPopup] = useState(false);
  const [error, setError] = useState('');
  // Channels of an in-flight generation → render a skeleton "creating" card.
  const [pendingChannels, setPendingChannels] = useState<ContentChannel[] | null>(null);

  const [articles, setArticles] = useState<ProposedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [rssOpen, setRssOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Monthly content-creation quota (news_watch = "Studio de Création"). limit
  // -1 = unlimited (enterprise/agency). `null` until /metering/usage resolves.
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(null);
  const [quotaDialog, setQuotaDialog] = useState(false);
  // Cursor stack: cursorStack[i] is the ExclusiveStartKey for page i (page 0 =
  // undefined). `page` is the current 0-based index; `nextCursor` is the key to
  // fetch page+1 (null when we're on the last page).
  const [page, setPage] = useState(0);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const genBaselineTopId = useRef<string | null>(null);
  // When arriving from the Product-content page (which already fired the launch),
  // we don't know the pre-launch top title — capture it on the first load so the
  // skeleton clears once a genuinely NEW article appears.
  const captureBaseline = useRef(false);

  const load = useCallback(async (targetPage = page) => {
    if (!activeBrand?.brandId) { setArticles([]); setLoading(false); return; }
    setLoading(true);
    try {
      const cursor = cursorStack[targetPage];
      const res = await proposedArticlesApi.page(activeBrand.brandId, {
        limit: PAGE_SIZE, cursor, includeArchived: showArchived,
      });
      setArticles(res.items);
      setNextCursor(res.nextCursor);
      // Clear the "creating" skeleton once a NEW article shows up at the top of
      // page 0 (newest-first, so a fresh article changes the top id). When we
      // arrived mid-flight (from Product content), the first load just captures
      // the pre-existing top id as the baseline rather than clearing.
      if (genBaselineTopId.current != null && targetPage === 0) {
        const topId = res.items[0]?.articleId ?? '';
        if (captureBaseline.current) {
          genBaselineTopId.current = topId;
          captureBaseline.current = false;
        } else if (topId !== genBaselineTopId.current) {
          setPendingChannels(null);
          genBaselineTopId.current = null;
        }
      }
    } catch (e: any) {
      setError(e.message || t('newsWatch.error.load'));
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId, page, cursorStack, showArchived, t]);

  // Monthly content-creation quota (tenant-wide). Refetched after each launch so
  // the remaining count + gate stay honest. Agency is credit-metered → no gauge.
  const loadQuota = useCallback(async () => {
    if (isAgency) { setQuota(null); return; }
    try {
      const res = await api.get<{ metrics: { metric: string; used: number; limit: number }[] }>('/metering/usage');
      const cc = res.metrics.find((m) => m.metric === 'content_creations');
      if (cc) setQuota({ used: cc.used, limit: cc.limit });
    } catch { /* non-blocking — the server gate is authoritative */ }
  }, [isAgency]);
  useEffect(() => { loadQuota(); }, [loadQuota]);

  // Reset to page 0 whenever the brand or the archived filter changes.
  useEffect(() => {
    setPage(0);
    setCursorStack([undefined]);
  }, [activeBrand?.brandId, showArchived]);

  // Load saved content topics (recommendations acted on) for the `reco` source
  // mode picker. Refetched after each launch so a just-used topic updates.
  const loadContentTopics = useCallback(async () => {
    if (!activeBrand?.brandId) { setContentTopics([]); return; }
    try {
      setContentTopics(await proposedArticlesApi.listContentTopics(activeBrand.brandId));
    } catch { /* non-blocking */ }
  }, [activeBrand?.brandId]);
  useEffect(() => { loadContentTopics(); }, [loadContentTopics]);

  // If arriving with ?source=reco (from an "Ajouter au studio" toast deep-link),
  // preselect the reco source mode so the fresh topic is front-and-center.
  useEffect(() => {
    if ((location.state as { focusReco?: boolean } | null)?.focusReco) {
      setSourceMode('reco');
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  // Arriving from the Product-content page: the launch already fired there, so
  // show the "en création" skeleton card immediately. We don't know the pre-launch
  // top id, so flag the next load to capture it as the baseline.
  useEffect(() => {
    const st = location.state as { pendingChannels?: ContentChannel[] } | null;
    if (st?.pendingChannels?.length) {
      setPendingChannels(st.pendingChannels);
      genBaselineTopId.current = '';
      captureBaseline.current = true;
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  useEffect(() => { load(); }, [load]);
  // Poll ONLY while a generation is in flight (a skeleton card is showing) and
  // we're on page 0 where it lands. Steady-state (nothing generating) does zero
  // background DDB reads — the old unconditional 20s poll billed every idle tab.
  useEffect(() => {
    if (!pendingChannels || page !== 0) return;
    const t = setInterval(() => load(0), 8_000);
    return () => clearInterval(t);
  }, [load, page, pendingChannels]);

  const goNext = () => {
    if (!nextCursor) return;
    const next = page + 1;
    setCursorStack((s) => { const c = [...s]; c[next] = nextCursor; return c; });
    setPage(next);
  };
  const goPrev = () => { if (page > 0) setPage((p) => p - 1); };

  // Update a card's status after an action-menu change, then reload the current
  // page (an archived card disappears when !showArchived). `createdAt` is passed
  // so the server does a single GetItem instead of a partition scan.
  const applyStatus = useCallback(async (articleId: string, createdAt: string | undefined, status: ProposedArticleStatus) => {
    if (!activeBrand?.brandId) return;
    try {
      await proposedArticlesApi.updateStatus(articleId, { brandId: activeBrand.brandId, status, createdAt });
      await load(page);
    } catch (e: any) {
      setError(e?.message || t('newsWatch.error.action'));
    }
  }, [activeBrand?.brandId, load, page, t]);

  const toggle = <T,>(list: T[], v: T): T[] =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const seedWords = seedWordCount(seed);
  const provideTooShort = sourceMode === 'provide' && !isProvideSeedSufficient(seed);
  const seedLimit = CONTENT_SEED_LIMITS[sourceMode];
  const seedOverLimit = seed.length > seedLimit;
  // Monthly content-creation quota (news_watch is a PULL feature — no cadence).
  // limit -1 = unlimited (enterprise/agency). Exhausted → the click opens the
  // upsell dialog instead of firing (kept clickable so the user sees WHY). The
  // server 429 QUOTA_EXCEEDED is the load-bearing gate; this is the UX mirror.
  const quotaExhausted = !!quota && quota.limit >= 0 && quota.used >= quota.limit;
  const quotaRemaining = quota && quota.limit >= 0 ? Math.max(0, quota.limit - quota.used) : null;
  const selectedTopic = contentTopics.find((t) => t.topicId === selectedTopicId) || null;
  const recoNotChosen = sourceMode === 'reco' && !selectedTopic;
  const canGenerate =
    channels.length > 0 && !provideTooShort && !seedOverLimit && !recoNotChosen && !launching;

  const handleGenerate = async () => {
    if (!activeBrand?.brandId || !canGenerate) return;
    // Proactive: quota already spent → show the upsell, don't hit the API.
    if (quotaExhausted) { setQuotaDialog(true); return; }
    setLaunching(true);
    setError('');
    const launchChannels = [...channels];
    const usingReco = sourceMode === 'reco' && selectedTopic;
    const config: { contentCreation: ContentCreationConfig } = {
      contentCreation: {
        sourceMode, format, channels, patterns, objective,
        ...(usingReco
          ? {
              recoTopic: selectedTopic.topic.slice(0, CONTENT_SEED_LIMITS.reco),
              ...(selectedTopic.originLabel ? { originLabel: selectedTopic.originLabel } : {}),
              ...(selectedTopic.sourceAuditId ? { sourceAuditId: selectedTopic.sourceAuditId } : {}),
              // Product-measurement recos carry their catalogue + real products so
              // the article grounds in verified URLs instead of inventing them.
              ...(selectedTopic.catalogueId ? { catalogueIds: [selectedTopic.catalogueId] } : {}),
              ...(selectedTopic.groundedProducts?.length
                ? { groundedProducts: selectedTopic.groundedProducts }
                : {}),
            }
          : { seed: seed.trim() || undefined }),
      },
    };
    try {
      await auditsApi.create('news_watch', config as any, activeBrand.brandId);
      // A reco topic is single-use per generation — mark it used so it drops out
      // of the picker. Awaited (item j) — a failure here just leaves the idea
      // visible again; loadContentTopics() below re-fetches regardless.
      if (usingReco) {
        try {
          await proposedArticlesApi.deleteContentTopic(selectedTopic.topicId, activeBrand.brandId);
        } catch {
          /* re-fetched below anyway */
        }
        setSelectedTopicId(null);
      }
      // Jump to page 0 (where the new article lands), snapshot the current top
      // article id so we can detect the new one, drop a skeleton card, reset the form.
      genBaselineTopId.current = articles[0]?.articleId ?? '';
      captureBaseline.current = false;
      setPendingChannels(launchChannels);
      setGenPopup(true);
      setSeed('');
      loadContentTopics();
      loadQuota(); // reflect the consumed creation in the remaining count
      if (page !== 0) { setPage(0); } else { setTimeout(() => load(0), 3000); }
    } catch (e: any) {
      if (e?.body?.code === 'QUOTA_EXCEEDED') {
        // Reactive (raced past the proactive check) — open the upsell dialog +
        // resync the count so the button reflects the exhausted state.
        setQuotaDialog(true);
        loadQuota();
      } else if (e?.status === 402 || e?.body?.code === 'INSUFFICIENT_CREDITS') {
        setError(t('newsWatch.error.insufficientCredits'));
      } else {
        setError(e.message || t('newsWatch.error.launch'));
      }
    } finally {
      setLaunching(false);
    }
  };

  const estimatedVariants = useMemo(() => {
    const LONGFORM = new Set(['blog_seo', 'medium', 'linkedin_article']);
    return channels.reduce((n, c) => n + (LONGFORM.has(c) ? 0 : c === 'linkedin_post' || c === 'x_thread' ? 2 : 1), 0);
  }, [channels]);

  const creditCost = channels.length * NEWS_WATCH_CHANNEL_COST;

  if (!activeBrand) {
    return <Box sx={{ p: 4 }}><Alert severity="info">{t('newsWatch.selectBrand')}</Alert></Box>;
  }

  return (
    <Box>
      <Box sx={{ px: 4, pt: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.5px">{t('newsWatch.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('newsWatch.subtitle')}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RssFeedIcon />} onClick={() => setRssOpen(true)}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {t('newsWatch.rssButton')}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' }, mt: 2, minHeight: 'calc(100vh - 160px)' }}>
        {/* ===== LEFT — create ===== */}
        <Box sx={{ p: 4, background: 'linear-gradient(180deg,#FBFAFF,#F7F4FE)', borderRight: '1px solid', borderColor: 'divider' }}>
          <PaneLabel color={PURPLE} text={t('newsWatch.pane.create')} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2.5, boxShadow: '0 1px 2px rgba(0,0,0,.04),0 6px 18px rgba(0,0,0,.05)' }}>
            <SectionTitle n={1} title={t('newsWatch.section.source')} />
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {SOURCE_MODES.map((m) => (
                <SourceCard key={m.value} icon={m.icon} title={t(m.title)} desc={t(m.desc)}
                  selected={sourceMode === m.value} onClick={() => setSourceMode(m.value)} />
              ))}
            </Stack>
            {sourceMode === 'provide' ? (
              <>
                <InputBase multiline minRows={3} fullWidth value={seed}
                  onChange={(e) => setSeed(e.target.value.slice(0, seedLimit))}
                  placeholder={t('newsWatch.provide.placeholder')} sx={inputSx} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: provideTooShort ? 'error.main' : 'text.secondary', fontStyle: 'italic' }}>
                    {provideTooShort
                      ? t('newsWatch.provide.tooShort', { min: CONTENT_PROVIDE_MIN_WORDS, count: seedWords })
                      : t('newsWatch.provide.wordCount', { count: seedWords })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{seed.length.toLocaleString()} / {seedLimit.toLocaleString()}</Typography>
                </Box>
              </>
            ) : sourceMode === 'reco' ? (
              <Box sx={{ mt: 1.5 }}>
                {contentTopics.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: 13 }}>
                    {t('newsWatch.reco.empty')}
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {contentTopics.map((t) => (
                      <TopicCard
                        key={t.topicId}
                        topic={t}
                        selected={selectedTopicId === t.topicId}
                        onSelect={() => setSelectedTopicId(t.topicId)}
                        onDelete={async () => {
                          if (!activeBrand?.brandId) return;
                          if (selectedTopicId === t.topicId) setSelectedTopicId(null);
                          setContentTopics((prev) => prev.filter((x) => x.topicId !== t.topicId));
                          try { await proposedArticlesApi.deleteContentTopic(t.topicId, activeBrand.brandId); }
                          catch { loadContentTopics(); }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
              <>
                <InputBase multiline minRows={2} fullWidth value={seed}
                  onChange={(e) => setSeed(e.target.value.slice(0, seedLimit))}
                  placeholder={t('newsWatch.propose.placeholder')} sx={inputSx} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('newsWatch.propose.hint')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{seed.length} / {seedLimit}</Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <SectionTitle n={2} title={t('newsWatch.section.length')} tag={t('newsWatch.tag.channelRelative')} />
            <ChipRow sx={{ mt: 1 }}>
              {FORMAT_OPTIONS.map((o) => (
                <SelChip key={o.value} selected={format === o.value} onClick={() => setFormat(o.value)} label={t(o.label)} sub={t(o.hint)} />
              ))}
            </ChipRow>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
              {t('newsWatch.lengthNote')}
            </Typography>

            <Divider sx={{ my: 2 }} />
            <SectionTitle n={3} title={t('newsWatch.section.channels')} tag={t('newsWatch.tag.multiple')} />
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {CHANNEL_GROUPS.map((g) => (
                <Box key={g.label}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .4, opacity: .8 }}>
                    {t(g.label)}
                  </Typography>
                  <ChipRow sx={{ mt: .5 }}>
                    {g.channels.map((c) => (
                      <SelChip key={c} selected={channels.includes(c)} onClick={() => setChannels((p) => toggle(p, c))}
                        label={CHANNEL_LABEL[c]} logo={<ChannelLogo channel={c} size={15} />} />
                    ))}
                  </ChipRow>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <SectionTitle n={4} title={t('newsWatch.section.pattern')} tag={t('newsWatch.tag.optionalMultiple')} />
            <ChipRow sx={{ mt: 1 }}>
              {PATTERN_OPTIONS.map((o) => (
                <SelChip key={o.value} selected={patterns.includes(o.value)} onClick={() => setPatterns((p) => toggle(p, o.value))}
                  label={t(o.label)} tip={t(o.tip)} />
              ))}
            </ChipRow>

            <Divider sx={{ my: 2 }} />
            <SectionTitle n={5} title={t('newsWatch.section.objective')} />
            <ChipRow sx={{ mt: 1 }}>
              {OBJECTIVE_OPTIONS.map((o) => (
                <SelChip key={o.value} selected={objective === o.value} onClick={() => setObjective(o.value)}
                  label={`${o.icon} ${t(o.label)}`} sub={t(o.hint)} />
              ))}
            </ChipRow>

            <Divider sx={{ my: 2 }} />
            {quotaExhausted && (
              <Alert severity="warning" sx={{ mb: 2 }}
                action={<Button color="inherit" size="small" onClick={() => navigate('/settings?tab=billing')}>{t('newsWatch.upgradeOffer')}</Button>}>
                <Trans i18nKey="newsWatch.quotaExhausted" count={quota?.limit ?? 0} components={{ b: <b /> }} />
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                <b>{t(FORMAT_OPTIONS.find((f) => f.value === format)?.label ?? '')}</b> · {t('newsWatch.summary.channels', { count: channels.length })}<br />
                <Trans i18nKey="newsWatch.summary.variants" count={estimatedVariants} components={{ b: <b /> }} />
                {isAgency ? (
                  <>
                    <br /><Trans i18nKey="newsWatch.summary.cost" values={{ credits: creditCost, channels: channels.length, per: NEWS_WATCH_CHANNEL_COST }} components={{ b: <b /> }} />
                  </>
                ) : quotaRemaining !== null && (
                  <><br /><Trans i18nKey="newsWatch.summary.remaining" count={quotaRemaining} components={{ b: <b /> }} /></>
                )}
              </Typography>
              <Tooltip arrow title={
                channels.length === 0 ? t('newsWatch.tooltip.selectChannel')
                : provideTooShort ? t('newsWatch.tooltip.provideMin', { min: CONTENT_PROVIDE_MIN_WORDS })
                : ''
              }>
                <span>
                  <Button variant="contained" size="large" disabled={!canGenerate}
                    startIcon={launching ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleGenerate} sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {t('newsWatch.generate')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* ===== RIGHT — library ===== */}
        <Box sx={{ p: 4, bgcolor: '#F4F5F8' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <PaneLabel color="#10B981" text={t('newsWatch.pane.library')} sx={{ mb: 0 }} />
            <FormControlLabel
              control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
              label={<Typography variant="caption" color="text.secondary">{t('newsWatch.showArchived')}</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>
          {loading && !pendingChannels ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : articles.length === 0 && !pendingChannels ? (
            <Box sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {showArchived ? t('newsWatch.emptyArchived') : t('newsWatch.empty')}
              </Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={1.5}>
                {pendingChannels && page === 0 && <GeneratingCard channels={pendingChannels} />}
                {articles.map((a) => (
                  <CreationCard key={a.articleId} article={a}
                    onClick={() => navigate(`/news-watch/article/${a.articleId}`)}
                    onStatus={(s) => applyStatus(a.articleId, a.createdAt, s)} />
                ))}
              </Stack>
              {(page > 0 || nextCursor) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Button size="small" startIcon={<ChevronLeftIcon />} disabled={page === 0 || loading}
                    onClick={goPrev} sx={{ textTransform: 'none' }}>{t('newsWatch.prev')}</Button>
                  <Typography variant="caption" color="text.secondary">{t('newsWatch.page', { page: page + 1 })}</Typography>
                  <Button size="small" endIcon={<ChevronRightIcon />} disabled={!nextCursor || loading}
                    onClick={goNext} sx={{ textTransform: 'none' }}>{t('newsWatch.next')}</Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <GenerationPopup open={genPopup} onClose={() => setGenPopup(false)} />
      <RssDialog open={rssOpen} onClose={() => setRssOpen(false)} brandId={activeBrand.brandId} />
      <QuotaDialog open={quotaDialog} onClose={() => setQuotaDialog(false)} limit={quota?.limit ?? 0}
        onUpgrade={() => { setQuotaDialog(false); navigate('/settings?tab=billing'); }} />
    </Box>
  );
}

// Shown when the monthly content-creation quota is spent. Routes to the billing
// tab where the user can upgrade to a plan with a larger monthly allotment.
// (Add-on packs are planned but not yet available — upgrade is the only path.)
function QuotaDialog({ open, onClose, limit, onUpgrade }: {
  open: boolean; onClose: () => void; limit: number; onUpgrade: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '50%', bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AutoAwesomeIcon sx={{ color: '#D97706', fontSize: 30 }} />
        </Box>
        <Typography variant="h6" fontWeight={800}>{t('newsWatch.quotaDialog.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
          <Trans i18nKey="newsWatch.quotaDialog.body" count={limit} components={{ b: <b /> }} />
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: 'center' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('newsWatch.later')}</Button>
          <Button variant="contained" onClick={onUpgrade} sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
            {t('newsWatch.upgradeOffer')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function RssDialog({ open, onClose, brandId }: { open: boolean; onClose: () => void; brandId: string }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (open && brandId) feedsApi.url(brandId).then((r) => setUrl(r.url)).catch(() => setUrl(''));
  }, [open, brandId]);
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* no-op */ } };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: .5 }}>{t('newsWatch.rss.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('newsWatch.rss.desc')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <InputBase value={url} readOnly fullWidth sx={{ ...inputSx, mt: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
          <Button variant="contained" onClick={copy} sx={{ whiteSpace: 'nowrap' }}>{copied ? t('newsWatch.copied') : t('newsWatch.copy')}</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          {t('newsWatch.rss.note')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onClose}>{t('newsWatch.close')}</Button>
        </Box>
      </Box>
    </Dialog>
  );
}

const inputSx = { mt: 1.5, width: '100%', border: '1.5px solid #E8E6F0', borderRadius: 2, px: 1.5, py: 1, fontSize: 14, '&:focus-within': { borderColor: PURPLE } };

function PaneLabel({ color, text, sx }: { color: string; text: string; sx?: any }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, ...sx }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: .5, textTransform: 'uppercase', color: color === PURPLE ? '#6929D6' : 'text.secondary' }}>{text}</Typography>
    </Box>
  );
}

function SectionTitle({ n, title, tag }: { n: number; title: string; tag?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{n} · {title}</Typography>
      {tag && <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', bgcolor: '#F2F0F7', px: .9, py: .1, borderRadius: 999 }}>{tag}</Typography>}
    </Box>
  );
}

function SourceCard({ icon, title, desc, selected, onClick }: { icon: string; title: string; desc: string; selected: boolean; onClick: () => void }) {
  return (
    <Box onClick={onClick} sx={{
      display: 'flex', gap: 1.5, alignItems: 'flex-start', border: '1.5px solid', borderColor: selected ? PURPLE : 'divider',
      borderRadius: 2, p: 1.4, cursor: 'pointer', bgcolor: selected ? '#F5F1FF' : '#fff',
      boxShadow: selected ? '0 0 0 3px rgba(124,58,237,.08)' : 'none', '&:hover': { borderColor: selected ? PURPLE : '#CFC8E5', bgcolor: '#F5F1FF' },
    }}>
      <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: selected ? PURPLE : 'divider', mt: .3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {selected && <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: PURPLE }} />}
      </Box>
      <Box sx={{ fontSize: 18 }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.4 }}>{desc}</Typography>
      </Box>
    </Box>
  );
}

function TopicCard({ topic, selected, onSelect, onDelete }: {
  topic: ContentTopic; selected: boolean; onSelect: () => void; onDelete: () => void;
}) {
  return (
    <Box onClick={onSelect} sx={{
      display: 'flex', gap: 1.25, alignItems: 'flex-start', border: '1.5px solid',
      borderColor: selected ? PURPLE : 'divider', borderRadius: 2, p: 1.25, cursor: 'pointer',
      bgcolor: selected ? '#F5F1FF' : '#fff', boxShadow: selected ? '0 0 0 3px rgba(124,58,237,.08)' : 'none',
      '&:hover': { borderColor: selected ? PURPLE : '#CFC8E5', bgcolor: '#F5F1FF' },
    }}>
      <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: selected ? PURPLE : 'divider', mt: .3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PURPLE }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.4 }}>{topic.topic}</Typography>
        {topic.originLabel && (
          <Typography sx={{ fontSize: 11.5, color: PURPLE, fontWeight: 600, mt: .25 }}>{topic.originLabel}</Typography>
        )}
      </Box>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ flexShrink: 0, mt: -.25 }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

function ChipRow({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ...sx }}>{children}</Box>;
}

function SelChip({ selected, onClick, label, sub, dot, logo, tip }: { selected: boolean; onClick: () => void; label: string; sub?: string; dot?: string; logo?: React.ReactNode; tip?: string }) {
  return (
    <Box onClick={onClick} sx={{
      display: 'inline-flex', alignItems: 'center', gap: .8, px: 1.6, py: .8, borderRadius: 999,
      border: '1.5px solid', borderColor: selected ? PURPLE : 'divider', bgcolor: selected ? PURPLE : '#fff',
      color: selected ? '#fff' : 'text.primary', cursor: 'pointer', fontSize: 13, fontWeight: 600,
      '&:hover': { borderColor: selected ? PURPLE : '#CFC8E5', bgcolor: selected ? PURPLE : '#F5F1FF' },
    }}>
      {logo && (
        <Box sx={{ display: 'inline-flex', width: 15, height: 15, borderRadius: '3px', overflow: 'hidden',
          // On the filled (selected) chip, wrap the logo in a white tile so brand
          // colors stay legible against the violet background.
          bgcolor: selected ? '#fff' : 'transparent', alignItems: 'center', justifyContent: 'center',
          p: selected ? '1px' : 0 }}>{logo}</Box>
      )}
      {dot && <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: dot, filter: selected ? 'brightness(0) invert(1)' : 'none' }} />}
      <span>{label}{sub && <Box component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 500, opacity: .75 }}>{sub}</Box>}</span>
      {tip && (
        <Tooltip title={tip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 14, ml: .3, opacity: .7 }} onClick={(e) => e.stopPropagation()} />
        </Tooltip>
      )}
    </Box>
  );
}

// `label` is an i18n KEY — resolve with `t(...)` at render.
const STATUS_META: Partial<Record<ProposedArticleStatus, { label: string; bg: string; fg: string }>> = {
  published: { label: 'newsWatch.status.published', bg: '#ECFDF5', fg: '#0B7A56' },
  dismissed: { label: 'newsWatch.status.archived', bg: '#F1F1F4', fg: '#6B7280' },
};

function CreationCard({ article, onClick, onStatus }: {
  article: ProposedArticle;
  onClick: () => void;
  onStatus: (s: ProposedArticleStatus) => void;
}) {
  const { t } = useTranslation();
  const art = article.article;
  const cfg = art?.config;
  const mom = art?.momentum ? MOMENTUM_META[art.momentum] : undefined;
  const status = article.status;
  const statusMeta = STATUS_META[status];
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const openMenu = (e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setMenuEl(e.currentTarget); };
  const closeMenu = (e?: React.MouseEvent) => { e?.stopPropagation(); setMenuEl(null); };
  const act = (e: React.MouseEvent, s: ProposedArticleStatus) => { e.stopPropagation(); setMenuEl(null); onStatus(s); };

  return (
    <Box onClick={onClick} sx={{
      bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, cursor: 'pointer',
      transition: 'all .15s', opacity: status === 'dismissed' ? 0.7 : 1,
      '&:hover': { borderColor: '#D7CEEC', boxShadow: '0 4px 14px rgba(0,0,0,.06)', transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>{article.title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{relativeDate(article.createdAt)}</Typography>
          <IconButton size="small" onClick={openMenu} sx={{ ml: .25 }}><MoreVertIcon fontSize="small" /></IconButton>
        </Box>
      </Box>
      {(() => {
        const origin = article.originLabel || (cfg as { originLabel?: string } | undefined)?.originLabel;
        return origin ? (
          <Typography variant="caption" sx={{ display: 'block', color: PURPLE, fontWeight: 600, mt: 0.5 }}>
            {origin}
          </Typography>
        ) : null;
      })()}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .6, alignItems: 'center', mt: 1 }}>
        {statusMeta && <Chip size="small" label={t(statusMeta.label)} sx={{ bgcolor: statusMeta.bg, color: statusMeta.fg, fontWeight: 700 }} />}
        {art?.wordCount ? <Chip size="small" label={t('newsWatch.wordsChip', { count: art.wordCount })} sx={{ bgcolor: '#F5F1FF', color: PURPLE, fontWeight: 600 }} /> : null}
        {cfg?.objective && <Chip size="small" label={t(OBJECTIVE_OPTIONS.find((o) => o.value === cfg.objective)?.label ?? '')} sx={{ bgcolor: '#ECFDF5', color: '#0B7A56', fontWeight: 600 }} />}
        {mom && <Chip size="small" label={t(mom.label)} sx={{ bgcolor: '#fff', border: `1px solid ${mom.color}`, color: mom.color, fontWeight: 600 }} />}
        {(cfg?.channels || []).map((c) => (
          <Box key={c} title={CHANNEL_LABEL[c]} sx={{ width: 22, height: 22, borderRadius: '5px', border: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChannelLogo channel={c} size={13} />
          </Box>
        ))}
      </Box>

      <Menu anchorEl={menuEl} open={!!menuEl} onClose={closeMenu as any}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {status !== 'published' && (
          <MenuItem onClick={(e) => act(e, 'published')}>
            <ListItemIcon><PublicIcon fontSize="small" /></ListItemIcon>
            {t('newsWatch.menu.publish')}
          </MenuItem>
        )}
        {status !== 'dismissed' ? (
          <MenuItem onClick={(e) => act(e, 'dismissed')}>
            <ListItemIcon><ArchiveOutlinedIcon fontSize="small" /></ListItemIcon>
            {t('newsWatch.menu.archive')}
          </MenuItem>
        ) : (
          <MenuItem onClick={(e) => act(e, 'proposed')}>
            <ListItemIcon><UnarchiveOutlinedIcon fontSize="small" /></ListItemIcon>
            {t('newsWatch.menu.restore')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// Shadow/skeleton card shown while a generation is in flight — already shows the
// chosen channel logos so the user sees what's being created.
function GeneratingCard({ channels }: { channels: ContentChannel[] }) {
  const { t } = useTranslation();
  return (
    <Box sx={{
      bgcolor: '#fff', border: '1px dashed', borderColor: '#EAE0FE', borderRadius: 3, p: 2,
      background: 'linear-gradient(90deg,#fff,#FBF8FF)', position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CircularProgress size={15} sx={{ color: PURPLE }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#6929D6' }}>{t('newsWatch.generating')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{t('newsWatch.justNow')}</Typography>
      </Box>
      <Skeleton variant="text" width="85%" height={22} />
      <Skeleton variant="text" width="55%" height={22} />
      <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', mt: 1 }}>
        {channels.map((c) => (
          <Box key={c} title={CHANNEL_LABEL[c]} sx={{ width: 22, height: 22, borderRadius: '5px', border: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChannelLogo channel={c} size={13} />
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: .5 }}>{t('newsWatch.generatingChannels', { count: channels.length })}</Typography>
      </Box>
    </Box>
  );
}

// Generation-in-progress popup. Dismissable — the work continues in the
// background and the skeleton card + bell notification track completion.
function GenerationPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4, p: 1, textAlign: 'center' } }}>
      <Box sx={{ p: 4 }}>
        <Box sx={{
          width: 76, height: 76, mx: 'auto', mb: 2.5, borderRadius: '50%',
          background: 'conic-gradient(from 0deg,#7C3AED,#B98CFF,#7C3AED)', position: 'relative',
          animation: 'nwspin 1.6s linear infinite', '@keyframes nwspin': { to: { transform: 'rotate(360deg)' } },
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box sx={{ position: 'absolute', inset: 8, borderRadius: '50%', bgcolor: '#fff' }} />
          <AutoAwesomeIcon sx={{ position: 'relative', zIndex: 1, color: PURPLE, fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={800}>{t('newsWatch.generating')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
          <Trans i18nKey="newsWatch.popup.body" components={{ b: <b />, br: <br /> }} />
        </Typography>
        <Box sx={{ mt: 2.5, p: 1.5, bgcolor: '#F2F0F7', borderRadius: 2, fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>
          <Trans i18nKey="newsWatch.popup.notify" components={{ b: <b /> }} />
        </Box>
        <Button onClick={onClose} sx={{ mt: 2, fontWeight: 600, textTransform: 'none' }}>{t('newsWatch.popup.continue')}</Button>
      </Box>
    </Dialog>
  );
}
